import React from 'react';
import { useTheme } from '../../contexts/ThemeContext';

/**
 * Horizontal tab selector with Webull-inspired styling
 * 
 * @param {Array} tabs - Array of {id, label, badge?, disabled?}
 * @param {string} activeTab - Currently active tab id
 * @param {Function} onChange - Called when tab is clicked
 * @param {string} variant - 'filled' or 'underline'
 */
const WebullStyleTabs = ({ tabs, activeTab, onChange, variant = 'filled', distribute = false }) => {
  const { colors } = useTheme();
  
  const containerStyle = variant === 'filled' ? {
    display: 'flex',
    gap: '2px',
    padding: '3px',
    border: `1px solid ${colors.border}`,
    borderRadius: '8px',
    backgroundColor: colors.bgPrimary,
    overflowX: distribute ? 'hidden' : 'auto',
    scrollbarWidth: 'none',
    WebkitOverflowScrolling: 'touch',
  } : {
    display: 'flex',
    gap: '16px',
    borderBottom: `1px solid ${colors.border}`,
    overflowX: 'auto',
    scrollbarWidth: 'none',
    WebkitOverflowScrolling: 'touch',
  };
  
  const getTabStyle = (isActive) => {
    if (variant === 'filled') {
      return {
        padding: '8px 16px',
        borderRadius: '6px',
        backgroundColor: isActive ? colors.primary : 'transparent',
        color: isActive ? '#fff' : colors.textMuted,
        fontWeight: isActive ? 600 : 500,
        fontSize: '14px',
        cursor: 'pointer',
        whiteSpace: 'nowrap',
        border: 'none',
        transition: 'all 0.15s ease',
        minWidth: distribute ? 0 : 'max-content',
        flex: distribute ? '1 1 0' : undefined,
        justifyContent: 'center',
        overflow: 'hidden',
      };
    } else {
      return {
        padding: '8px 0',
        color: isActive ? colors.primary : colors.textMuted,
        fontWeight: isActive ? 600 : 500,
        fontSize: '14px',
        cursor: 'pointer',
        whiteSpace: 'nowrap',
        border: 'none',
        borderBottom: isActive ? `3px solid ${colors.primary}` : 'none',
        backgroundColor: 'transparent',
        transition: 'all 0.15s ease',
        minWidth: 'max-content',
      };
    }
  };
  
  return (
    <div style={containerStyle} className="webull-tabs-container">
      <style>
        {`
          .webull-tabs-container::-webkit-scrollbar {
            display: none;
          }
        `}
      </style>
      {tabs.map(tab => (
        <button
          key={tab.id}
          onClick={() => !tab.disabled && onChange(tab.id)}
          disabled={tab.disabled}
          style={{
            ...getTabStyle(activeTab === tab.id),
            opacity: tab.disabled ? 0.5 : 1,
            cursor: tab.disabled ? 'not-allowed' : 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: 6,
          }}
        >
          {tab.label}
          {tab.badgeElement ? tab.badgeElement : (
            tab.badge !== undefined && tab.badge !== null && (
              <span style={{ 
                marginLeft: 6,
                opacity: 0.8,
              }}>
                ({tab.badge})
              </span>
            )
          )}
          {tab.rightElement && tab.rightElement}
        </button>
      ))}
    </div>
  );
};

export default WebullStyleTabs;
